import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Text, TextInput, Checkbox, i18n, tokens, localizationMaps, Services } from '@simplybenefits/sb-ui-library';
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";

class MetaClaimInputModal extends PureComponent {
    state = {
        patient: this.props.meta ? this.props.meta.patient : this.props.user.isDependent ? `${ this.props.user.firstname } ${ this.props.user.lastname } (${ localizationMaps.fromEnglish("relationships", this.props.user.relationship) })` : `${ this.props.user.firstname } ${ this.props.user.lastname }`,
        category: this.props.meta ? this.props.meta.category : this.props.type === 'Lifestyle' ? (i18n.t(tokens.titles.lifestyleSpending)) : i18n.t(tokens.titles.paramedicals),
        service: this.props.meta ? this.props.meta.service : this.props.type === 'Lifestyle' ? (localizationMaps.fromEnglish('benefits', this.props.lsaClaimTypes?.[0]) || 'All') : i18n.t(tokens.dropdowns.benefits.acupuncturist),
        provider: this.props.meta && this.props.meta.provider ? this.props.meta.provider : null,
        serviceOptions: this.props.type === 'Lifestyle' ? (this.props.lsaClaimTypes || []) : this.props.hsaClaimTypes.Paramedicals.reverse(),
        patientOptions: this.props.user.isDependent ? [ `${ this.props.user.firstname } ${ this.props.user.lastname } (${ this.props.user.relationship })` ] : [ `${ this.props.user.firstname } ${ this.props.user.lastname }` ],
        acceptedHSATerms: false,
        acceptedHSATermsError: false
    };

    /**
     * @returns {Promise<void>}
     */
    async componentDidMount() {
        await this._saveData();

        if (this.props.dependents && !this.props.user.isDependent) {
            const dependents = await this.props.dependents.map((dependent) => `${ dependent.firstname } ${ dependent.lastname } (${ localizationMaps.fromEnglish("relationships", dependent.relationship) })`);

            this.setState({
                patientOptions: [ ...this.state.patientOptions, ...dependents ]
            });
        }
    }

    _selectPatient = async (event) => {
        await this.setState({
            patient: event.target.value
        });

        this._saveData();
    };

    _selectCategory = async (event) => {
        await this.setState({
            category: event.target.value
        });

        this._setServiceOptions(event.target.value);
        this._saveData();
    };

    _selectService = async (event) => {
        await this.setState({
            service: event.target.value
        });

        this._saveData();
    };

    _handleTermsChange = () => {
        this.setState({
            acceptedHSATerms: !this.state.acceptedHSATerms
        });
    };

    _handleProviderChange = (value) => {
        this.setState({
            provider: value
        });
    };

    _saveData = () => {
        this.props.saveData({
            ...this.props.meta,
            patient: this.state.patient,
            category: this.state.category,
            service: this.state.service,
            provider: this.state.provider
        }, 'meta');
    };

    _next = async () => {
        if (this.props.type === 'HealthSpending' && !this.state.acceptedHSATerms) {
            this.setState({
                acceptedHSATermsError: i18n.t(tokens.labels.claims.firstSubmitClaims)
            });

            return;
        }

        await this._saveData();
        this.props.next();
    };

    _setServiceOptions = (category) => {
        category = localizationMaps.toEnglish("categories", category);

        const options = this.props.type === 'Lifestyle' ? (this.props.lsaClaimTypes || []) : this.props.hsaClaimTypes[category].reverse();

        this.setState({
            serviceOptions: options,
            service: localizationMaps.fromEnglish("benefits", options[0])
        });
    };

    render() {
        return (
            <Modal
                testid="submit-claim-modal"
                aria-labelledby="Submit Claim"
                open={true}
                onClose={ this.props.onClose }
                title={this.props.title}
                onBack={ this.props.onBack }
                loader={ this.props.loader }
                primaryButton={{
                    label: i18n.t(tokens.buttons.next),
                    onClick: this._next
                }}
            >
                <Text mediumLarge margin="10px 0 0 0">
                    { i18n.t(tokens.labels.claims.patient) }:
                </Text>

                <TextInput
                    label={ i18n.t(tokens.labels.claims.selectPatient) }
                    variant="outlined"
                    value={ this.state.patient }
                    onChange={ this._selectPatient }
                    select
                    required
                    fullWidth
                    selectOptions={ this.state.patientOptions }
                />

                {
                    this.props.type === 'Lifestyle' ?
                        <React.Fragment>
                            {
                                !this.state.serviceOptions?.length ?
                                    <Text medium align="center" margin="10px 0 0 0">
                                        { i18n.t(tokens.labels.claims.noLifestyleSpendingServicesDefined) }
                                    </Text>
                                    :
                                    <React.Fragment>
                                        <Text mediumLarge margin="10px 0 0 0">
                                            { i18n.t(tokens.labels.claims.service) }:
                                        </Text>
                                        <TextInput
                                            label={ i18n.t(tokens.labels.claims.selectService) }
                                            variant="outlined"
                                            value={this.state.service}
                                            onChange={this._selectService}
                                            select
                                            required
                                            fullWidth
                                            selectOptions={ this.state.serviceOptions.map((serviceOption) => localizationMaps.fromEnglish("benefits", serviceOption.trim())) }
                                        />
                                    </React.Fragment>
                            }
                        </React.Fragment>
                        :
                        <React.Fragment>
                            <Text mediumLarge margin="10px 0 0 0">
                                { i18n.t(tokens.labels.claims.category) }:
                            </Text>

                            <TextInput
                                label={ i18n.t(tokens.labels.claims.selectCategory) }
                                variant="outlined"
                                value={this.state.category}
                                onChange={this._selectCategory}
                                select
                                required
                                fullWidth
                                selectOptions={ Services.selectFieldOptions.categoryOptions() }
                            />

                            <Text mediumLarge margin="10px 0 0 0">
                                { i18n.t(tokens.labels.claims.service) }:
                            </Text>

                            <TextInput
                                label={ i18n.t(tokens.labels.claims.selectService) }
                                variant="outlined"
                                value={this.state.service}
                                onChange={this._selectService}
                                select
                                required
                                fullWidth
                                selectOptions={ this.state.serviceOptions.map((serviceOption) => localizationMaps.fromEnglish("benefits", serviceOption.trim())) }
                            />
                        </React.Fragment>
                }

                {
                    (this.props.type !== 'Lifestyle' && this.props.type !== 'HealthSpending') &&
                            <React.Fragment>
                                <Text medium>
                                    { i18n.t(tokens.labels.claims.quickReimbursement) }
                                </Text>

                                <TextInput
                                    label={ i18n.t(tokens.labels.claims.clinicNameLabel) }
                                    variant="outlined"
                                    value={ this.state.provider || ''}
                                    onChange={(e) => this._handleProviderChange(e.target.value)}
                                    fullWidth
                                    testid="clinicname"
                                />
                            </React.Fragment>
                }

                {
                    this.props.type === 'HealthSpending' &&
                            <FormControl error={ Boolean(this.state.acceptedHSATermsError) }>
                                <FormGroup
                                    row
                                    style={{ marginTop: 20 }}
                                >
                                    <FormControlLabel
                                        style={{ margin: 0 }}
                                        control={
                                            <Checkbox
                                                checked={ this.state.acceptedHSATerms }
                                                onChange={ this._handleTermsChange }
                                                value="acceptedHSATerms"
                                                color="secondary"
                                            />
                                        }
                                        label={ <Text avMedium>{ i18n.t(tokens.labels.claims.submitClaimAcknowledgment, { patient: this.state.patient }) }</Text>}
                                    />
                                </FormGroup>

                                {
                                    this.state.acceptedHSATermsError &&
                                        <FormHelperText>{ this.state.acceptedHSATermsError }</FormHelperText>
                                }
                            </FormControl>
                }

                <Text light tiny>
                    { i18n.t(tokens.labels.claims.claimAgreementScreen2) }
                </Text>
            </Modal>
        );
    }
}

MetaClaimInputModal.propTypes = {
    whitelabel: PropTypes.object,
    user: PropTypes.object,
    dependents: PropTypes.array,
    hsaClaimTypes: PropTypes.object,
    lsaClaimTypes: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    user: state.user.meta,
    dependents: state.user.dependents,
    hsaClaimTypes: state.usage.data.claimTypes,
    lsaClaimTypes: state.spendingAccounts.lsaClaimTypes
});

export default connect(mapStateToProps)(MetaClaimInputModal);